import { useCallback, useEffect, useState } from 'react';
import { ErreurApi, verifications, type EtatVerification, type Verification } from './api-noyau';

/**
 * MOWIBO VERIFY (§16) — l'écran public.
 *
 * ⚠️ §17 LE SÉPARE DE L'ENVIRONNEMENT PROFESSIONNEL, ET CE N'EST PAS
 * COSMÉTIQUE : « Le citoyen ne rentre pas dans l'environnement administratif
 * Cadastral. Si une vérification publique est proposée, elle passe par une
 * interface contrôlée de type Verify, sans exposer l'environnement
 * professionnel. » D'où une SECONDE entrée Vite (`verifier.html`) : le paquet
 * servi au public ne contient ni la carte, ni les mutations, ni le coffre — pas
 * même leur code mort. Une rubrique derrière la connexion aurait livré tout le
 * paquet professionnel à qui ouvre la page.
 *
 * ⚠️ RÈGLE DE FOND, §16 : « Ne jamais transformer une vérification technique en
 * certification juridique automatique. » L'avertissement n'est donc pas une
 * mention légale en bas de page : il est DANS le résultat, à côté du verdict.
 *
 * ⚠️ QUATRE ÉTATS, ET « INCONNU » N'EST PAS « INEXISTANT ». Le cadastre est en
 * cours de numérisation. Afficher « cette parcelle n'existe pas » sur une
 * référence non encore versée ferait annuler des ventes légitimes.
 */

type Genre = 'parcelle' | 'annonce' | 'document';

const GENRES: Array<{
  cle: Genre; onglet: string; libelle: string; exemple: string; aide: string;
}> = [
  {
    cle: 'parcelle', onglet: 'Une parcelle', libelle: 'Référence cadastrale',
    exemple: 'GN-M25-26301-42698',
    aide: "Le numéro qui figure sur un plan, un acte ou une attestation. Il commence par le code du pays.",
  },
  {
    cle: 'annonce', onglet: 'Une annonce', libelle: "Identifiant de l'annonce",
    exemple: '00000000-0000-0000-0000-000000000000',
    aide: "L'identifiant du bien tel qu'il figure sur la place de marché. Il ne dit rien de la personne qui vend.",
  },
  {
    cle: 'document', onglet: 'Un document', libelle: 'Code du QR',
    exemple: 'le code lu sur le document',
    aide: "Scannez le QR du document : votre téléphone ouvre directement cette page avec le code rempli.",
  },
];

/**
 * ⚠️ CINQ ÉTATS AFFICHÉS, PAS QUATRE. Le cahier en nomme quatre ; le service en
 * rend un cinquième, `revoque`, pour un document retiré par son autorité. Le
 * replier sur « incohérence » dirait qu'une contradiction a été trouvée, ce qui
 * est faux et affaiblit le message : un acte révoqué EST une information nette.
 */
const ETATS: Record<EtatVerification, { classe: string; titre: string; sens: string }> = {
  verifie: {
    classe: 'v-ok', titre: 'Vérifié',
    sens: "Les points listés ci-dessous ont été contrôlés dans le registre et concordent.",
  },
  declare: {
    classe: 'v-attente', titre: 'Vérification complémentaire nécessaire',
    sens: "La donnée existe mais tout n'a pas pu être confirmé. Ce n'est ni une validation, ni un rejet.",
  },
  incoherent: {
    classe: 'v-alerte', titre: 'Incohérence détectée',
    sens: "Deux informations du registre se contredisent. Ne concluez rien sans l'avis de l'autorité cadastrale.",
  },
  non_verifiable: {
    classe: 'v-inconnu', titre: 'Information non accessible',
    sens: "Le registre n'a rien pu confirmer. Cela ne veut PAS dire que la chose n'existe pas.",
  },
  revoque: {
    classe: 'v-alerte', titre: 'Document révoqué',
    sens: "Ce document a été retiré par l'autorité qui l'a émis. Il ne doit plus être opposé.",
  },
};

/** Les vérifications que le cahier prévoit et que le registre ne sait PAS faire. */
const PAS_ENCORE = [
  { quoi: 'Une adresse', pourquoi: "l'adressage est un chantier distinct (Mowibo Maps) — aucune donnée d'adresse n'est encore versée ici" },
  { quoi: 'Une valeur immobilière', pourquoi: 'aucune transaction n’est encore constatée dans le registre : il n’y a rien à comparer' },
];

export function Verifier({ dansLAtelier = false }: { dansLAtelier?: boolean }) {
  const [genre, setGenre] = useState<Genre>('parcelle');
  const [saisie, setSaisie] = useState('');
  const [resultat, setResultat] = useState<Verification | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [encours, setEncours] = useState(false);

  const lancer = useCallback(async (g: Genre, valeur: string) => {
    const v = valeur.trim();
    if (!v) return;
    setEncours(true); setErreur(null); setResultat(null);
    try {
      const r = g === 'parcelle' ? await verifications.parcelle(v)
              : g === 'annonce'  ? await verifications.annonce(v)
              :                    await verifications.document(v);
      setResultat(r);
    } catch (err) {
      // ⚠️ ON DISTINGUE LA PANNE DU RÉSULTAT. Un 429 (trop de vérifications),
      // un 400 (identifiant mal formé) et une coupure réseau ne sont PAS des
      // verdicts sur le bien : les afficher comme « non vérifiable » ferait
      // croire au citoyen que le registre a répondu.
      if (err instanceof ErreurApi) {
        setErreur(
          err.statut === 429 ? "Trop de vérifications depuis ce poste. Réessayez dans une minute — ce n'est pas un résultat."
          : err.statut === 0 ? "Le service est injoignable depuis cet appareil. Aucune vérification n'a eu lieu."
          : err.statut === 400 ? `Cet identifiant n'a pas la forme attendue. ${err.message}`
          : `Le registre n'a pas pu répondre (${err.statut}). Aucune vérification n'a eu lieu.`);
      } else setErreur("Aucune vérification n'a eu lieu.");
    } finally { setEncours(false); }
  }, []);

  // Le QR du document ouvre cette page avec le code déjà rempli.
  useEffect(() => {
    if (dansLAtelier) return;
    const q = new URLSearchParams(window.location.search);
    for (const g of ['document', 'parcelle', 'annonce'] as Genre[]) {
      const v = q.get(g);
      if (v) { setGenre(g); setSaisie(v); void lancer(g, v); return; }
    }
  }, [dansLAtelier, lancer]);

  const actif = GENRES.find((g) => g.cle === genre)!;

  return (
    <div className={dansLAtelier ? 'verif verif-atelier' : 'verif'}>
      <header className="verif-tete">
        <h1>Vérifier avant de payer</h1>
        <p className="verif-chapeau">
          Contrôlez une référence cadastrale, une annonce ou un document auprès du
          registre foncier de Guinée. <strong>Gratuit, sans compte, et sans donner
          votre identité.</strong>
        </p>
      </header>

      {dansLAtelier && (
        <p className="verif-note">
          Vous regardez la vue <strong>publique</strong>. Elle n'expose aucune donnée
          nominative : elle dit qu'un droit existe, jamais au nom de qui.
          {' '}<a href="/verifier" target="_blank" rel="noreferrer">Ouvrir la page publique</a>.
        </p>
      )}

      <div className="verif-onglets" role="tablist">
        {GENRES.map((g) => (
          <button
            key={g.cle} role="tab" aria-selected={genre === g.cle}
            className={genre === g.cle ? 'actif' : ''}
            onClick={() => { setGenre(g.cle); setResultat(null); setErreur(null); }}
          >{g.onglet}</button>
        ))}
      </div>

      <form className="verif-champ" onSubmit={(e) => { e.preventDefault(); void lancer(genre, saisie); }}>
        <label htmlFor="verif-saisie">{actif.libelle}</label>
        <p className="verif-aide">{actif.aide}</p>
        <div className="verif-ligne">
          <input
            id="verif-saisie" value={saisie} onChange={(e) => setSaisie(e.target.value)}
            placeholder={actif.exemple} autoComplete="off" spellCheck={false}
          />
          <button className="verif-bouton" disabled={encours || !saisie.trim()}>
            {encours ? 'Vérification…' : 'Vérifier'}
          </button>
        </div>
      </form>

      {erreur && (
        <div className="verif-panne" role="alert">
          <strong>Aucune vérification n'a eu lieu.</strong>
          <p>{erreur}</p>
        </div>
      )}

      {resultat && <Resultat r={resultat} />}

      <section className="verif-limites">
        <h2>Ce que cette page ne fait pas</h2>
        <ul>
          <li>
            <strong>Elle ne délivre aucun titre.</strong> Une réponse « Vérifié » atteste
            l'état du registre à cet instant — ni la propriété d'une personne, ni
            l'absence de charges, ni la validité d'une vente.
          </li>
          <li>
            <strong>Elle ne nomme personne.</strong> Le registre dit qu'un droit est
            inscrit ; il ne dit jamais au nom de qui. C'est volontaire.
          </li>
          {PAS_ENCORE.map((p) => (
            <li key={p.quoi}>
              <strong>{p.quoi} ne peut pas encore être vérifiée</strong> — {p.pourquoi}.
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Resultat({ r }: { r: Verification }) {
  const e = ETATS[r.etat] ?? ETATS.non_verifiable;
  const points = r.verifie ?? [];
  return (
    <section className={`verif-resultat ${e.classe}`} aria-live="polite">
      <header>
        <span className="verif-pastille">{r.pastille ?? ''}</span>
        <div>
          <h2>{e.titre}</h2>
          <p className="verif-sens">{e.sens}</p>
        </div>
      </header>

      {r.identifiant && <p className="verif-objet">Référence contrôlée : <code>{r.identifiant}</code></p>}
      {r.parcelle ? <p className="verif-objet">Parcelle rattachée : <code>{String(r.parcelle)}</code></p> : null}
      {r.message && <p className="verif-message">{r.message}</p>}

      {points.length > 0 && (
        <>
          <h3>Ce qui a été contrôlé</h3>
          <ul className="verif-points">
            {points.map((p) => (
              <li key={p.point} className={p.resultat ? 'oui' : 'non'}>
                <span aria-hidden>{p.resultat ? '✓' : '✕'}</span>
                <span className="sr">{p.resultat ? 'contrôlé, conforme :' : 'contrôlé, non conforme :'}</span>
                <b>{p.point}</b>
                {p.observe && <em>{p.observe}</em>}
              </li>
            ))}
          </ul>
        </>
      )}

      {r.document && (
        <>
          <h3>Le document désigné</h3>
          <dl className="verif-doc">
            {Object.entries(r.document).map(([k, v]) => (
              <div key={k}><dt>{k.replace(/_/g, ' ')}</dt><dd>{String(v ?? '—')}</dd></div>
            ))}
          </dl>
          {r.empreinte_sha256 && (
            <p className="verif-empreinte">
              Empreinte du fichier authentique :<br />
              <code>{String(r.empreinte_sha256)}</code>
              <span>Comparez-la à celle du fichier en votre possession. Deux fichiers
              qui diffèrent d'un octet ont deux empreintes sans rapport.</span>
            </p>
          )}
        </>
      )}

      {/* ⚠️ CE QU'ON NE SAIT PAS EST AFFICHÉ AUSSI VISIBLEMENT QUE CE QU'ON SAIT.
          Une absence silencieuse se lit « il n'y a rien à signaler ». */}
      {(r.non_accessible?.length || r.non_verifiable?.length) ? (
        <>
          <h3>Ce que le registre ne dit pas</h3>
          <ul className="verif-muet">
            {[...(r.non_accessible ?? []), ...(r.non_verifiable ?? [])].map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </>
      ) : null}

      {r.recommandation && <p className="verif-conseil">{r.recommandation}</p>}

      <p className="verif-avertissement">
        {r.avertissement ??
          "Cette réponse porte sur les données du registre à l'instant de la consultation. Elle ne vaut ni titre de propriété, ni certificat juridique, ni garantie de transaction."}
      </p>
    </section>
  );
}
